export interface GpaRange {
  p25: number;
  p50: number;
  p75: number;
}

export type GpaMatch = 'above' | 'middle' | 'near' | 'below' | 'none';

/**
 * Classify a student's GPA relative to a campus/institution 25th–75th percentile
 * range. Mirrors the inline logic originally used in the benchmark tables.
 *
 * - `none` when no student GPA is provided.
 * - `above` when at/above the 75th percentile.
 * - `middle` when at/above the 25th percentile (and below the 75th).
 * - `near`  when within 10% below the 25th percentile.
 * - `below` otherwise.
 */
export function classifyGpaMatch(
  gpa: number | null,
  range: GpaRange,
): GpaMatch {
  if (gpa === null) return 'none';
  if (gpa >= range.p75) return 'above';
  if (gpa >= range.p25) return 'middle';
  if (gpa >= range.p25 * 0.9) return 'near';
  return 'below';
}

export type ShortlistBand = 'reach' | 'match' | 'safety';

/**
 * Map an admissions match classification onto a shortlist band.
 *
 * | GPA match | Shortlist band |
 * |-----------|----------------|
 * | above     | safety         |
 * | middle    | match          |
 * | near      | reach          |
 * | below     | reach          |
 * | none      | reach          |
 */
export function matchToShortlistBand(match: GpaMatch): ShortlistBand {
  switch (match) {
    case 'above':
      return 'safety';
    case 'middle':
      return 'match';
    case 'near':
    case 'below':
    case 'none':
    default:
      return 'reach';
  }
}
